import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { SubTasksWebComponent } from '../component/SubTasksWebComponent';
import { SubTasksRepository } from '../repository/SubTasksRepository';
import { SubTasksView } from '../view/SubTasksView';
import { templateSubTasks } from '../view/template';
import { GridEvents } from '../../../grid/types/common';
import {
  SubTasksEvents,
  Dependencies,
  SubTaskItem,
  EndDateMessageWillShowWhenIsThisStatus,
  ErrorMessageWillShowWhenIsThisStatus,
  SubTasksConstants,
} from '../types';
import {
  TaskType,
  KnowledgeBaseArticles,
  TaskErrorCodes,
  TaskStatus,
  getTaskStatusLabel,
  SubmitToSandboxTaskErrorCodes,
} from '../../networkTasks/types';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { showErrorToast, showSuccessToast } from '../../../ui/gzToastNotification';
import { SubTaskDetailsPanelWebComponent } from '../../subTaskDetailsPanel';
import moment from 'moment/moment';
import { QuarantineActionStatus } from '../../../quarantine/exchangeProtection/types';
import { PanelDetailsProps } from '../../subTaskDetailsPanel/types';
import { dateFormatter } from '@nirvana/shared/time';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { translateFn } from '../../../utils/translator.helper';
import { getS3ObjectUrl } from '../helpers/utils';

export class SubTasksController extends BaseWebComponentController {
  public component: SubTasksWebComponent;
  public dataRepository: SubTasksRepository;
  public view: SubTasksView;
  public viewConstructor: typeof SubTasksView;
  public dataRepositoryConstructor: typeof SubTasksRepository;
  public dependencies: Dependencies;

  private selectedSubTasks: Array<SubTaskItem> = [];

  constructor(component: SubTasksWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: SubTasksView,
      viewTemplate: templateSubTasks,
      dataRepositoryConstructor: SubTasksRepository,
    });
  }

  public computeEventListeners() {
    return [
      {
        element: this.view.dom.$grid,
        event: GridEvents.SELECT_ROWS,
        callback: this.onSelectionChanged,
      },
      {
        element: this.view.dom.$stopBtn,
        event: 'click',
        callback: this.onStopButtonClick,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROW_CLICKED,
        callback: this.handleRowClicked,
      },
      {
        element: this.component,
        event: SubTasksEvents.CLOSE_SUBTASK_DETAILS_PANEL,
        callback: this.closeDetailsPanel,
      },
      {
        element: this.component,
        event: SubTasksEvents.OPEN_RANSOMWARE_GRID,
        callback: this.openRansomwareGrid,
      },
      {
        element: this.view.dom.$btnBack,
        event: 'click',
        callback: this.onBackButtonClick,
      },
    ];
  }

  public async handleRowClicked(event: WebComponentEvent) {
    if (!this.view.dom.$grid.enableRowClickEvent) {
      return;
    }

    // nothing to do if no row is clicked
    if (!event.detail.rowDetails) {
      return;
    }

    const content = this.buildDetailsPanel(event);
    this.view.dom.$sideMenu.setMenuContent(content);
    this.view.dom.$sideMenu.openSideMenu();
  }

  public async onStopButtonClick(ev: WebComponentEvent) {
    this.showStopConfirmationDialog(this.onStopButtonConfirmationDialogConfirm.bind(this));
  }

  public toggleStopButton() {
    let disabled = false;

    this.selectedSubTasks.forEach((task: SubTaskItem) => {
      // check if the user is the sub-task's owner
      if (task.ownerId !== this.dependencies.getCurrentUser()._id) {
        disabled = true;
      }

      // check if sub-task's status is 'in progress'
      if (task.status !== TaskStatus.STATUS_PROCESSING) {
        disabled = true;
      }

      // check if sub-task's type is allowed to be stopped
      if (task.type !== TaskType.TASK_TYPE_SCAN_CHILD && task.type !== TaskType.TASK_TYPE_SCAN_EXCHANGE_CHILD) {
        disabled = true;
      }
    });

    this.view.dom.$stopBtn.disabled = disabled;
  }

  public onSelectionChanged(ev: WebComponentEvent) {
    this.selectedSubTasks = ev.detail.selectedItemsData;

    if (this.selectedSubTasks.length === 0) {
      this.view.dom.$stopBtn.disabled = true;
      return;
    }

    this.toggleStopButton();
  }

  private showStopConfirmationDialog(confirmCallback) {
    const windowTitle = this.dependencies.translateFn('Stop');

    let windowBody;
    if (this.selectedSubTasks.length === 1) {
      windowBody = this.dependencies.translateFn('Are you sure you want to stop the selected sub-task?');
    } else {
      windowBody = this.dependencies.translateFn('Are you sure you want to stop the selected sub-tasks?');
    }

    const windowFooter = this.view.getStopConfirmationDialogFooterHtml();

    GzDialogWebComponent.computeCustomDialog(windowBody, windowFooter, windowTitle, null, confirmCallback);
  }

  private getParentTaskTypeFromSubTaskType(subTask: SubTaskItem) {
    switch (subTask.type) {
      case TaskType.TASK_TYPE_SCAN_CHILD:
        return TaskType.TASK_TYPE_STOP_SCAN_PARENT;
      case TaskType.TASK_TYPE_SCAN_EXCHANGE_CHILD:
        return TaskType.TASK_TYPE_STOP_SCAN_EXCHANGE_PARENT;
      default:
        throw new Error(this.dependencies.translateFn('Illegal item selected'));
    }
  }

  private async onStopButtonConfirmationDialogConfirm(ev: WebComponentEvent) {
    if (this.selectedSubTasks.length === 0) {
      showErrorToast(this.dependencies.translateFn('No item selected'));
      return;
    }

    // initialize messages
    let removeError, unknownErrorMessage, successfulMessage;
    if (this.selectedSubTasks.length === 1) {
      successfulMessage = this.dependencies.translateFn('Sub-task stopped successfully');
      unknownErrorMessage = this.dependencies.translateFn('Sub-task has not stopped. Please retry.');
    } else {
      successfulMessage = this.dependencies.translateFn('Sub-tasks stopped successfully');
      unknownErrorMessage = this.dependencies.translateFn('Sub-tasks have not stopped. Please retry.');
    }

    try {
      // set the parent's task type for the selected sub-tasks
      const stopTaskType: number = this.getParentTaskTypeFromSubTaskType(this.selectedSubTasks[0]);

      // try to perform request
      const response = await this.dataRepository.createTasks(
        this.selectedSubTasks,
        stopTaskType,
        this.dependencies.translateFn('Stop Scan task')
      );

      // if the createTasks method returns 0, then no task was created, then signal an error
      if (!response) {
        removeError = unknownErrorMessage;
      }
    } catch (error) {
      removeError = error.message || unknownErrorMessage;
    }

    // when an error appears, then display error toast
    if (removeError) {
      showErrorToast(removeError);
      return;
    }

    showSuccessToast(successfulMessage);

    this.refreshGrid();
  }

  private refreshGrid() {
    this.view.dom.$grid.controller?.handleGridItemsLoad();
  }

  /**
   * This method builds the details panel, sets the translations for labels and sets the values of the details.
   *
   * @param {WebComponentEvent} event
   * @returns {SubTaskDetailsPanelWebComponent}
   * @private
   */
  private buildDetailsPanel(event: WebComponentEvent) {
    const record = event.detail.rowDetails;
    const panel = new SubTaskDetailsPanelWebComponent();

    this.setDetailsLabels(panel);
    this.setDetailsValues(panel, record);
    this.setSubTaskCustomDetailsToPanel(record, panel);

    return panel;
  }

  /**
   * If the task has custom details, this method will append the custom details to the details panel
   *
   * @param record
   * @param panel
   * @private
   */
  private setSubTaskCustomDetailsToPanel(record, panel) {
    const panelDetailsProps: PanelDetailsProps = panel.panelDetailsProps;
    panel.subTask = record;

    switch (record.type) {
      case TaskType.TASK_TYPE_SAVE_QUARANTINE_EXCHANGE_CHILD:
        this.setDetailsForSaveQuarantineExchangeChildTaskType(panelDetailsProps, record);
        break;

      case TaskType.TASK_TYPE_DEPLOY_NUTANIX_SVA_CHILD:
        this.setDetailsForDeployNutanixSvaChildTaskType(panelDetailsProps, record);
        break;

      // BEST install task
      case TaskType.TASK_TYPE_INSTALL_ENDPOINT_CHILD:
      case TaskType.TASK_TYPE_INSTALL_ENDPOINT_SUPA_CHILD:
        this.setDetailsForInstallEndpointChildTaskType(panelDetailsProps, record);
        break;

      case TaskType.TASK_TYPE_BUILD_DETONATION_IMAGE_CHILD:
        this.setDetailsForBuildDetonationImageChildTaskType(panelDetailsProps, record);
        break;

      case TaskType.TASK_TYPE_RANSOMWARE_RESTORE_FILES_CHILD:
        this.setDetailsForRansomwareRestoreFilesChildTaskType(panel, record);
        break;

      case TaskType.TASK_TYPE_MOVE_ENDPOINT_CHILD:
        this.setDetailsForMoveEndpointChildTaskType(panelDetailsProps, record);
        break;

      case TaskType.TASK_TYPE_RETRIEVE_QUARANTINE_FILE_CHILD:
        this.setDetailsForRetrieveQuarantineChildTaskType(panelDetailsProps, record);
        break;

      case TaskType.TASK_TYPE_PATCH_INSTALL_CHILD:
      case TaskType.TASK_TYPE_PATCH_SCAN_CHILD:
        this.setDetailsForPatchManagementChildTaskTypes(panelDetailsProps, record);
        break;

      case TaskType.TASK_TYPE_SUBMIT_TO_SANDBOX_CHILD:
        this.setDetailsForSubmitToSandboxChildTaskType(panel, record);
        break;

      case TaskType.TASK_TYPE_SUSPEND_PROTECTION_CHILD:
        this.setDetailsForSuspendProtectionChildTaskType(panelDetailsProps);
        break;

      case TaskType.TASK_TYPE_IOR_FIX_CHILD:
        this.setDetailsForIorFixChildTaskType(panel, record);
        break;

      case TaskType.TASK_TYPE_IOR_ROLLBACK_CHILD:
        this.setDetailsForIorRollbackChildTaskType(panel, record);
        break;

      case TaskType.TASK_TYPE_RUN_LIVE_SEARCH_QUERY_CHILD:
        this.setDetailsForRunLiveSearchQueryChildTaskType(panel, record);
        break;

      case TaskType.TASK_TYPE_UPDATE_BEHAVIOR_ACTIONS_CHILD:
        this.setDetailsForPhasrActionsChildTaskType(panel, record);
        break;
    }
  }

  private setDetailsForIorFixChildTaskType(panel, record) {
    panel.panelDetailsProps.riskManagementExtraDetails = {
      title: translateFn('Fix action status'),
      routines: record.specifics?.routines ?? [],
    };
  }

  private setDetailsForIorRollbackChildTaskType(panel, record) {
    panel.panelDetailsProps.riskManagementExtraDetails = {
      title: translateFn('Rollback action status'),
      routines: record.specifics?.routines ?? [],
    };
  }

  private setDetailsForPhasrActionsChildTaskType(panel, record) {
    panel.panelDetailsProps.updateBehavioralProfilesDetails = {
      users: record.specifics?.userNames ?? [],
    };
  }

  private computeStatusMessage(record: any): string {
    let statusMessage = '';
    const hasError = record.specifics && record.specifics.errorCode > 0;
    const isScheduledTask =
      record.specifics && record.specifics.scheduler && record.specifics.scheduler.scheduledDeploy;
    const isRescheduledTaskWhichFailed = isScheduledTask && hasError && record.status === TaskStatus.STATUS_PENDING;
    const showStatusMessage = [TaskStatus.STATUS_STOPPED, TaskStatus.STATUS_PROCESSED].indexOf(record.status) !== -1;

    if (record.status === TaskStatus.STATUS_PROCESSED) {
      statusMessage = translateFn('Task finished successfully.');
    } else if (
      record.status === TaskStatus.STATUS_STOPPED &&
      !record.specifics?.errorMessage &&
      record.specifics?.endTime &&
      record.specifics?.startTime &&
      (new Date(record.specifics?.endTime).getTime() - new Date(record.specifics?.startTime).getTime()) / 36e5 > 48
    ) {
      statusMessage = translateFn('The task took more than 48 hours to complete.');
    } else if (record.specifics?.errorMessage && (showStatusMessage || isRescheduledTaskWhichFailed)) {
      statusMessage = record.specifics.errorMessage;
    }

    return statusMessage;
  }

  private closeDetailsPanel() {
    this.view.dom.$sideMenu.closeSideMenu();
    this.view.dom.$sideMenu.setMenuContent(null);
  }

  /**
   * For 'TASK_TYPE_RANSOMWARE_RESTORE_FILES_CHILD' task type, when clicking on 'More details' in details panel
   *
   * @param {WebComponentEvent} event
   * @private
   */
  private openRansomwareGrid(event: WebComponentEvent) {
    this.dependencies.openRansomwareGrid(event.detail.rowDetails);
  }

  private setDetailsLabels(panel: SubTaskDetailsPanelWebComponent) {
    panel.panelConfigProps = {
      closeButtonLabel: this.view.allTranslations.common.close.toUpperCase(),
    };
  }

  private setDetailsValues(panel: SubTaskDetailsPanelWebComponent, record: any) {
    panel.panelDetailsProps.endpointName = record.targets[0].name;
    panel.panelDetailsProps.status = getTaskStatusLabel(record.status, this.dependencies.translateFn);
    panel.panelDetailsProps.company = record.companies[0].name;

    if (record.targets[0].integrationName) {
      panel.panelDetailsProps.awsSubscriptionName = record.targets[0].integrationName;
    }

    // extract container information, if it exists
    const containers = record.targets[0]?.displayEntities;
    const containerNames = containers?.map(({ name }) => name);
    panel.panelDetailsProps.containerNames = containerNames ? containerNames?.join(', ') : '';

    if (record.startDate) {
      panel.panelDetailsProps.createdOn = moment(record.startDate).format(SubTasksConstants.FORMAT_DATE_AND_TIME);
    }

    if (record.endDate && EndDateMessageWillShowWhenIsThisStatus.includes(record.status)) {
      panel.panelDetailsProps.endedOn = moment(record.endDate).format(SubTasksConstants.FORMAT_DATE_AND_TIME);
    } else if (record.specifics.endTime && EndDateMessageWillShowWhenIsThisStatus.includes(record.status)) {
      panel.panelDetailsProps.endedOn = moment(record.specifics.endTime).format(SubTasksConstants.FORMAT_DATE_AND_TIME);
    }

    if (record.specifics?.scheduler?.scheduledDeploy && record.status === TaskStatus.STATUS_PENDING) {
      this.setSchedulerDetails(panel.panelDetailsProps, record);
    }

    panel.panelDetailsProps.statusMessage = this.computeStatusMessage(record);
  }

  private setSchedulerDetails(panelDetailsProps: PanelDetailsProps, record) {
    if (!isEmpty(record.specifics.scheduler?.nextRun)) {
      const nextTimeRun = dateFormatter(record.specifics.scheduler.nextRun, this.dependencies.getTimezone());
      const currentExactTime = dateFormatter(new Date().getTime(), this.dependencies.getTimezone());
      let scheduledValue = '';
      if (new Date(nextTimeRun).getTime() <= new Date(currentExactTime).getTime()) {
        scheduledValue = this.dependencies.translateFn('On next power on');
      } else {
        scheduledValue = moment(record.specifics.scheduler.nextRun).format(SubTasksConstants.FORMAT_DATE_AND_TIME);
      }

      if (record.specifics && record.specifics?.errorCode > 0) {
        panelDetailsProps.scheduledLabel = this.dependencies.translateFn('Rescheduled for');
        panelDetailsProps.scheduled = scheduledValue;
      } else {
        panelDetailsProps.scheduledLabel = this.dependencies.translateFn('Postponed until');
        panelDetailsProps.scheduled = scheduledValue;
      }
    }
  }

  private setDetailsForSaveQuarantineExchangeChildTaskType(panelDetailsProps: PanelDetailsProps, record) {
    if (record.specifics !== undefined && ErrorMessageWillShowWhenIsThisStatus.includes(record.status)) {
      let message = '';
      if (record.specifics.errorMessage !== undefined && record.specifics.errorMessage) {
        panelDetailsProps.statusMessage = record.specifics.errorMessage;
      }
      // search for failed items
      const quarantineItems = record.specifics.items;
      let saveFailedCount = 0;
      quarantineItems.forEach(item => {
        if (item.action === QuarantineActionStatus.ACTION_STATUS_FAILED_SAVE) {
          saveFailedCount++;
        }
      });
      if (saveFailedCount === quarantineItems.length && saveFailedCount) {
        message = this.dependencies.translateFn('Could not save the selected quarantined items to {path}', {
          path: record.specifics.settings.path,
        });
      } else if (saveFailedCount > 0) {
        message = this.dependencies.translateFn('Could not save some of the selected quarantined items to {path}', {
          path: record.specifics.settings.path,
        });
      } else {
        message = this.dependencies.translateFn('All selected quarantined items were successfully saved to {path}', {
          path: record.specifics.settings.path,
        });
      }

      panelDetailsProps.linkToErrorCodesLabel = message;
    }
  }

  private setDetailsForRansomwareRestoreFilesChildTaskType(panel: SubTaskDetailsPanelWebComponent, record) {
    if (record.status === TaskStatus.STATUS_STOPPED) {
      panel.panelDetailsProps.statusMessage = '';
      panel.subTask = record;
      panel.panelDetailsProps.ransomwareRestoreFilesMessage = {
        message: this.dependencies.translateFn(
          'The task could not complete successfully. One or more files could not be restored due to one of these reasons:'
        ),
        reasons: [
          this.dependencies.translateFn('The endpoint was offline'),
          this.dependencies.translateFn('The files are no longer available'),
        ],
      };
    }
  }

  private setDetailsForDeployNutanixSvaChildTaskType(panelDetailsProps, record) {
    const language = this.dependencies.getUserLanguage();
    if (
      record.status === TaskStatus.STATUS_STOPPED &&
      Number(record.specifics.errorCode) === TaskErrorCodes.ERROR_CODE_SVA_IMAGE_COULD_NOT_BE_DEPLOYED
    ) {
      panelDetailsProps.linkToErrorCodesText = this.dependencies.translateFn(
        'Learn about Security Server deployment in Nutanix.'
      );
      panelDetailsProps.linkToErrorCodesUrl =
        typeof KnowledgeBaseArticles.supportForNutanixSvaDeployLinks[language] !== 'undefined'
          ? KnowledgeBaseArticles.supportForNutanixSvaDeployLinks[language]
          : KnowledgeBaseArticles.supportForNutanixSvaDeployLinks['default'];
    }
  }

  private setDetailsForInstallEndpointChildTaskType(panelDetailsProps, record) {
    if (record.status === TaskStatus.STATUS_STOPPED) {
      panelDetailsProps.linkToErrorCodesLabel = this.dependencies.translateFn(
        'For more details and troubleshooting steps, refer to the article below or contact your system administrator.'
      );
      panelDetailsProps.linkToErrorCodesText = this.dependencies.translateFn('BEST deployments errors');
      panelDetailsProps.linkToErrorCodesUrl = KnowledgeBaseArticles.supportForBestDeployLinksBaseUrl;
    }
  }

  private setDetailsForBuildDetonationImageChildTaskType(panelDetailsProps, record) {
    const language = this.dependencies.getUserLanguage();
    if (record.status === TaskStatus.STATUS_STOPPED) {
      panelDetailsProps.linkToErrorCodesLabel = this.dependencies.translateFn(
        'For more details and troubleshooting steps, refer to the article below or contact your system administrator.'
      );
      panelDetailsProps.linkToErrorCodesText = this.dependencies.translateFn('Error Codes for Image Management');
      panelDetailsProps.linkToErrorCodesUrl =
        KnowledgeBaseArticles.sandboxAnalyzer.imageManagementErrorsLinks[language];
    }
  }

  private setDetailsForMoveEndpointChildTaskType(panelDetailsProps, record) {
    const taskCompanies = record.companies;
    const sourceCompanyName = taskCompanies.length > 0 ? taskCompanies[0].name : '';
    panelDetailsProps.sourceCompany = sourceCompanyName;
    panelDetailsProps.destinationCompany = record.specifics.destinationCompany?.name;
  }

  private setDetailsForRetrieveQuarantineChildTaskType(panelDetailsProps: PanelDetailsProps, record): void {
    panelDetailsProps.quarantineFileName = record.specifics.archiveName;
  }

  private setDetailsForPatchManagementChildTaskTypes(panelDetailsProps, record) {
    const documentedErrorCodes = [
      TaskErrorCodes.ERROR_CODE_PATCH_TASK_13,
      TaskErrorCodes.ERROR_CODE_PATCH_TASK_21,
      TaskErrorCodes.ERROR_CODE_PATCH_TASK_87,
      TaskErrorCodes.ERROR_CODE_PATCH_TASK_126,
      TaskErrorCodes.ERROR_CODE_PATCH_TASK_995,
      TaskErrorCodes.ERROR_CODE_PATCH_TASK_1150,
      TaskErrorCodes.ERROR_CODE_PATCH_TASK_1247,
      TaskErrorCodes.ERROR_CODE_PATCH_TASK_2147942450,
      TaskErrorCodes.ERROR_CODE_PATCH_TASK_1,
      TaskErrorCodes.ERROR_CODE_PATCH_TASK_160,
    ];

    if (
      record.status === TaskStatus.STATUS_STOPPED &&
      documentedErrorCodes.includes(Number(record.specifics.errorCode))
    ) {
      panelDetailsProps.linkToErrorCodesLabel = this.dependencies.translateFn(
        'For more details and troubleshooting steps, refer to the article below or contact your system administrator.'
      );
      panelDetailsProps.linkToErrorCodesText = this.dependencies.translateFn('Patch Management errors');

      if (this.dependencies.isCloud()) {
        panelDetailsProps.linkToErrorCodesUrl = Application.Main.company.isPartner()
          ? KnowledgeBaseArticles.supportForPatchManagementErrorCodesCloudPartner
          : KnowledgeBaseArticles.supportForPatchManagementErrorCodesCloudCustomer;
      } else {
        panelDetailsProps.linkToErrorCodesUrl = KnowledgeBaseArticles.supportForPatchManagementErrorCodesOnPremise;
      }
    }
  }

  private setDetailsForSubmitToSandboxChildTaskType(panel: SubTaskDetailsPanelWebComponent, record: any) {
    const panelDetailsProps: PanelDetailsProps = panel.panelDetailsProps;

    panelDetailsProps.paths = record.specifics.files.map((file: any) => file.path);
    if (isEmpty(panelDetailsProps.paths)) {
      panelDetailsProps.paths = [translateFn('None')];
    }

    panelDetailsProps.commandLines = record.specifics.execute.map((ex: any) => ex.commandLine);
    if (isEmpty(panelDetailsProps.commandLines)) {
      panelDetailsProps.commandLines = [translateFn('None')];
    }

    panelDetailsProps.submitToSandboxInvalidFiles = record.specifics.invalidFiles ?? null;

    this.setInvalidFilesInSubmitToSandboxTaskDetails(panel, record);
  }

  private setDetailsForRunLiveSearchQueryChildTaskType(panel: SubTaskDetailsPanelWebComponent, record: any) {
    const panelDetailsProps: PanelDetailsProps = panel.panelDetailsProps;
    const { query, s3UploadConfig } = record.specifics;

    panelDetailsProps.runLiveSearchQueryExtraDetails = {
      query: query,
      s3Bucket: s3UploadConfig.bucket,
      s3Region: s3UploadConfig.region,
      s3ObjectUrl: getS3ObjectUrl(s3UploadConfig.region, s3UploadConfig.bucket, s3UploadConfig.fileLocation)
    }
  }

  private setInvalidFilesInSubmitToSandboxTaskDetails(panel: SubTaskDetailsPanelWebComponent, record: any) {
    if (!record.specifics.invalidFiles) {
      panel.panelDetailsProps.submitToSandboxInvalidFiles = null;
      return;
    }

    const unknownError = translateFn('Unknown error.');

    const fileErrorMessage = {
      [SubmitToSandboxTaskErrorCodes.ERROR_NEGATIVE_1]: unknownError,
      [SubmitToSandboxTaskErrorCodes.ERROR_6]: translateFn('File not found.'),
      [SubmitToSandboxTaskErrorCodes.ERROR_7]: translateFn('File bigger than 100 MB.'),
      [SubmitToSandboxTaskErrorCodes.ERROR_8]: translateFn('File is empty.'),
    };

    panel.panelDetailsProps.submitToSandboxInvalidFiles = record.specifics.invalidFiles.map(file => ({
      path: file.path,
      error: fileErrorMessage[file.error] ?? unknownError,
    }));
  }

  private setDetailsForSuspendProtectionChildTaskType(panelDetailsProps: PanelDetailsProps) {
    panelDetailsProps.suspendDuration = this.dependencies.task.specifics?.duration ?? null;
  }

  private onBackButtonClick() {
    this.component.dispatchEvent(new CustomEvent('backButtonClick'));
  }
}
